import type React from 'react'
import { useCallback, useReducer } from 'react'
import every from 'lodash/every'
import { useDeepCompareEffect } from '../useDeepCompareEffect'
import filter from 'lodash/filter'
import some from 'lodash/some'

export type EntityWithSelection<T> = T & {
  _selected: boolean
}

type State<T> = {
  list: EntityWithSelection<T>[]
  hasSelectedAll: boolean
  hasSelected: boolean
  selectedList: EntityWithSelection<T>[]
}

type Action<T> =
  | { type: 'SET_LIST'; payload: T[] }
  | { type: 'TOGGLE_SELECT_ALL' }
  | { type: 'TOGGLE_SELECT_BY'; payload: (value: EntityWithSelection<T>, index: number, array: EntityWithSelection<T>[]) => boolean }

const reducer = <T>(state: State<T>, action: Action<T>): State<T> => {
  let updatedList = [] // Temporary variable to hold the updated list

  switch (action.type) {
    case 'SET_LIST': {
      const itemsWithChecked = action.payload.map((item) => ({ ...item, _selected: false }))
      return {
        ...state,
        list: itemsWithChecked,
        hasSelectedAll: false,
        hasSelected: false,
        selectedList: [], // Reset selectedList
      }
    }
    case 'TOGGLE_SELECT_ALL': {
      updatedList = state.list.map((item) => ({ ...item, _selected: !state.hasSelectedAll }))
      return {
        ...state,
        list: updatedList,
        hasSelectedAll: !state.hasSelectedAll,
        hasSelected: !state.hasSelectedAll,
        selectedList: !state.hasSelectedAll ? updatedList : [], // Update selectedList based on toggle
      }
    }
    case 'TOGGLE_SELECT_BY': {
      updatedList = [...state.list]
      const filteredResult = filter(updatedList, action.payload)
      const selected = !some(filteredResult, { _selected: true })

      updatedList.forEach((item) => {
        if (filteredResult.includes(item)) {
          item._selected = selected
        }
      })

      const newSelectedList = updatedList.filter((item) => item._selected) // Compute new selectedList

      return {
        ...state,
        list: updatedList,
        hasSelectedAll: every(updatedList, { _selected: true }),
        hasSelected: some(updatedList, { _selected: true }),
        selectedList: newSelectedList, // Update selectedList
      }
    }
    default:
      return state
  }
}
export type ToggleSelectByFunction<T = any> = (
  predicate: (value: EntityWithSelection<T>, index: number, array: EntityWithSelection<T>[]) => boolean
) => void
export type UseListWithSelection<T = any> = {
  list: EntityWithSelection<T>[]
  toggleSelectAll: () => void
  toggleSelectBy: ToggleSelectByFunction<T>
  hasSelectedAll: boolean
  hasSelected: boolean
  selectedList: EntityWithSelection<T>[]
  reset: (sourceList: T[]) => void
}

export function useListWithSelection<T = unknown>(sourceList: T[] = []): UseListWithSelection<T> {
  const [{ list, hasSelectedAll, hasSelected, selectedList }, dispatch] = useReducer<React.Reducer<State<T>, Action<T>>>(reducer, {
    list: [],
    hasSelectedAll: false,
    hasSelected: false,
    selectedList: [],
  })

  useDeepCompareEffect(() => {
    reset(sourceList)
  }, [sourceList])

  const toggleSelectAll = useCallback(() => dispatch({ type: 'TOGGLE_SELECT_ALL' }), [])

  const toggleSelectBy = useCallback((predicate: (value: EntityWithSelection<T>, index: number, array: EntityWithSelection<T>[]) => boolean) => {
    dispatch({ type: 'TOGGLE_SELECT_BY', payload: predicate })
  }, [])

  const reset = useCallback((sourceList: T[]) => {
    dispatch({ type: 'SET_LIST', payload: sourceList })
  }, [])

  return {
    list,
    toggleSelectAll,
    toggleSelectBy,
    hasSelectedAll,
    hasSelected,
    selectedList,
    reset,
  }
}
